import React from "react";

import BuildIcon from "@mui/icons-material/Build";
import CodeIcon from "@mui/icons-material/Code";

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { BuildLogJob } from "src/domains/application/module/api/IApplicationApi";

import {
  BUILD_PHASE_STEPS,
  CI_JOB_STEPS,
  DEPLOY_PHASE_STEPS,
  EVENT_STEP_MAP,
} from "./job-detail-constants";
import { formatEventType } from "./shared/pipeline-helpers";

export { formatEventType };
export { STATUS_CHIP_COLOR } from "./shared/status-constants";

export interface ServiceDeployStatus {
  name: string;
  type: string;
  deployMethod: string;
  status: "pending" | "in_progress" | "completed" | "failed";
  url?: string | null;
}

export function groupDeployEventsByService(
  events: DeploymentEvent[],
  templateServices?: { name: string; type: string }[],
): ServiceDeployStatus[] {
  const serviceMap = new Map<string, ServiceDeployStatus>();

  if (templateServices) {
    for (const svc of templateServices) {
      const deployMethod =
        svc.type === "static" ? "CDN Upload" : "K8s Deployment";
      serviceMap.set(svc.name, {
        name: svc.name,
        type: svc.type,
        deployMethod,
        status: "pending",
      });
    }
  }

  for (const event of events) {
    const svcName = event.metadata?.serviceName as string | undefined;
    if (!svcName) continue;
    const existing = serviceMap.get(svcName);
    const baseType =
      existing?.type ?? (event.metadata?.serviceType as string) ?? "unknown";
    const deployMethod =
      baseType === "static" ? "CDN Upload" : "K8s Deployment";

    if (
      event.eventType.includes("deploy_service_started") ||
      event.eventType.includes("cdn_deploy_started")
    ) {
      serviceMap.set(svcName, {
        name: svcName,
        type: baseType,
        deployMethod: existing?.deployMethod ?? deployMethod,
        status: "in_progress",
      });
    } else if (
      event.eventType.includes("deploy_service_completed") ||
      event.eventType.includes("cdn_deploy_completed")
    ) {
      serviceMap.set(svcName, {
        name: svcName,
        type: baseType,
        deployMethod: existing?.deployMethod ?? deployMethod,
        status: "completed",
        url: event.metadata?.url as string | null,
      });
    } else if (
      event.eventType.includes("deploy_service_failed") ||
      event.eventType.includes("cdn_deploy_failed")
    ) {
      serviceMap.set(svcName, {
        name: svcName,
        type: baseType,
        deployMethod: existing?.deployMethod ?? deployMethod,
        status: "failed",
      });
    }
  }

  return Array.from(serviceMap.values());
}

// ── Task Step Types ──────────────────────────────────────────────────────────

export interface TaskStep {
  key: string;
  label: string;
  icon: React.ReactElement;
  status:
    "completed" | "in_progress" | "failed" | "pending" | "queued" | "skipped";
  startedAt?: string | null;
  completedAt?: string | null;
  logLines?: string[];
  htmlUrl?: string;
  logRef?: string | null;
}

export interface PersistedExecution {
  id: number;
  name: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  logRef: string | null;
}

/**
 * Parse the canonical MinIO log object key used by build/deploy services.
 * Returning null for a mismatched saga prevents a child execution from
 * accidentally loading another run's logs.
 */
export function parsePipelineLogRef(
  logRef: string,
  sagaId: string,
): { service: string; phase: string } | null {
  const normalized = logRef.startsWith("minio:")
    ? logRef.slice("minio:".length)
    : logRef;
  const match = normalized.match(/^logs\/([^/]+)\/([^/]+)\/(.+)\.log$/);
  if (!match || match[1] !== sagaId) return null;
  return {
    service: decodeURIComponent(match[2]),
    phase: decodeURIComponent(match[3]),
  };
}

export function derivePersistedExecutionSteps(
  executions: PersistedExecution[],
  logLines: string[] = [],
  logLinesByExecutionId?: ReadonlyMap<number, string[]>,
): TaskStep[] {
  return executions.map((execution) => {
    const executionLines = logLinesByExecutionId?.has(execution.id)
      ? (logLinesByExecutionId.get(execution.id) ?? [])
      : logLines;
    return {
      key: `execution-${execution.id}`,
      label: execution.name,
      icon: <CodeIcon />,
      status:
        execution.status === "completed" || execution.status === "success"
          ? "completed"
          : execution.status === "failed"
            ? "failed"
            : execution.status === "in_progress" ||
                execution.status === "running"
              ? "in_progress"
              : execution.status === "skipped"
                ? "skipped"
                : "pending",
      startedAt: execution.startedAt,
      completedAt: execution.completedAt,
      logLines: executionLines,
      logRef: execution.logRef,
    };
  });
}

export function deriveLiveLogSteps(
  liveLogs: Map<string, string[]>,
  step: string,
): TaskStep[] {
  const phaseMap = step === "deploy" ? DEPLOY_PHASE_STEPS : BUILD_PHASE_STEPS;
  const steps: TaskStep[] = [];

  for (const [phase, lines] of liveLogs) {
    const config = phaseMap[phase] ?? {
      label: phase.charAt(0).toUpperCase() + phase.slice(1),
      icon: <BuildIcon />,
    };
    const hasError = lines.some(
      (l) =>
        l.toLowerCase().includes("error") || l.toLowerCase().includes("failed"),
    );
    const isRunning = lines.some(
      (l) =>
        l.toLowerCase().includes("running") ||
        l.toLowerCase().includes("building"),
    );
    let status: TaskStep["status"] = "completed";
    if (hasError) status = "failed";
    else if (isRunning) status = "in_progress";
    else if (lines.length === 0) status = "pending";

    steps.push({
      key: phase,
      label: config.label,
      icon: config.icon,
      status,
      logLines: lines,
    });
  }

  return steps;
}

export function deriveCIJobSteps(
  buildLogJobs: BuildLogJob[],
  qualityGates?: BuildLogJob[],
): TaskStep[] {
  const steps: TaskStep[] = [];

  if (qualityGates && qualityGates.length > 0) {
    for (const job of qualityGates) {
      const config = CI_JOB_STEPS[job.jobName] ?? {
        label:
          job.jobName.charAt(0).toUpperCase() +
          job.jobName.slice(1).replace(/[-_]/g, " "),
        icon: <CodeIcon />,
      };
      steps.push({
        key: `qg-${job.jobName}`,
        label: config.label,
        icon: config.icon,
        status:
          job.conclusion === "success"
            ? "completed"
            : job.conclusion === "failure"
              ? "failed"
              : job.status === "in_progress"
                ? "in_progress"
                : job.status === "queued"
                  ? "queued"
                  : "pending",
        startedAt: job.startedAt,
        completedAt: job.completedAt,
        logLines: job.logLines,
        htmlUrl: job.htmlUrl,
      });
    }
  }

  for (const job of buildLogJobs) {
    if (qualityGates?.some((qg) => qg.jobName === job.jobName)) continue;
    const config = CI_JOB_STEPS[job.jobName] ??
      BUILD_PHASE_STEPS[job.jobName] ?? {
        label:
          job.jobName.charAt(0).toUpperCase() +
          job.jobName.slice(1).replace(/[-_]/g, " "),
        icon: <BuildIcon />,
      };
    steps.push({
      key: `job-${job.jobId}`,
      label: config.label,
      icon: config.icon,
      status:
        job.conclusion === "success"
          ? "completed"
          : job.conclusion === "failure"
            ? "failed"
            : job.status === "in_progress"
              ? "in_progress"
              : job.status === "queued"
                ? "queued"
                : "pending",
      startedAt: job.startedAt,
      completedAt: job.completedAt,
      logLines: job.logLines,
      htmlUrl: job.htmlUrl,
    });
  }

  return steps;
}

export function formatStepDuration(
  startedAt?: string | null,
  completedAt?: string | null,
): string | null {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const sec = Math.round((end - start) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function deriveEventSteps(events: DeploymentEvent[]): TaskStep[] {
  const seen = new Map<string, DeploymentEvent>();
  for (const ev of events) {
    const svc = (ev.metadata?.serviceName as string) ?? "";
    const key = `${ev.eventType}:${svc}`;
    if (!seen.has(key) || ev.createdAt > seen.get(key)!.createdAt) {
      seen.set(key, ev);
    }
  }

  const steps: TaskStep[] = [];
  for (const ev of seen.values()) {
    const config = EVENT_STEP_MAP[ev.eventType];
    const svcName = ev.metadata?.serviceName as string | undefined;

    const label = config
      ? config.useNameFromMeta && svcName
        ? `${config.label} ${svcName}`
        : config.label
      : formatEventType(ev.eventType);
    const icon = config ? config.icon : <CodeIcon />;

    const logLines: string[] = [];
    if (ev.severity === "error" && typeof ev.metadata?.error === "string") {
      logLines.push(ev.metadata.error as string);
    }
    if (ev.message && ev.message !== ev.status) {
      logLines.push(ev.message);
    }

    const hasRealStartedAt = ev.startedAt != null && ev.startedAt !== "";
    const hasRealCompletedAt = ev.completedAt != null && ev.completedAt !== "";

    steps.push({
      key: ev.id,
      label,
      icon,
      status:
        ev.status === "completed"
          ? "completed"
          : ev.status === "failed"
            ? "failed"
            : ev.status === "in_progress"
              ? "in_progress"
              : "pending",
      startedAt: hasRealStartedAt ? ev.startedAt : undefined,
      completedAt: hasRealCompletedAt ? ev.completedAt : undefined,
      logLines: logLines.length > 0 ? logLines : undefined,
    });
  }

  steps.sort(
    (a, b) =>
      new Date(a.startedAt ?? 0).getTime() -
      new Date(b.startedAt ?? 0).getTime(),
  );
  return steps;
}

// ── Panel-level helpers ───────────────────────────────────────────────────────

/**
 * Filter deployment events for the active view.
 *
 * - Level 2 (service selected inside a group): filter by that service.
 * - Non-group node with a specific serviceName: keep phase-level events
 *   (no serviceName in metadata) or events matching the node's service.
 * - Group node (level 1) or VCS node: show all events for the step.
 */
export function filterStepEvents(
  events: DeploymentEvent[],
  step: string,
  isGroupNode: boolean,
  activeSvc: string | null,
  serviceName?: string,
): DeploymentEvent[] {
  return events.filter((e) => {
    if (e.step !== step) return false;
    if (activeSvc) {
      return (e.metadata?.serviceName as string | undefined) === activeSvc;
    }
    if (!isGroupNode && serviceName) {
      const svcInMeta = e.metadata?.serviceName as string | undefined;
      return !svcInMeta || svcInMeta === serviceName;
    }
    return true;
  });
}

/**
 * Derive the task steps for a service, preferring live logs → CI jobs → events.
 */
export function deriveSteps(
  liveLogs: Map<string, Map<string, string[]>> | undefined,
  activeServiceName: string,
  step: string,
  buildLogJobs: BuildLogJob[] | undefined,
  qualityGates: BuildLogJob[] | undefined,
  stepEvents: DeploymentEvent[],
): TaskStep[] {
  const svcLiveLogs = liveLogs?.get(activeServiceName);
  if (svcLiveLogs && svcLiveLogs.size > 0)
    return deriveLiveLogSteps(svcLiveLogs, step);
  if (
    step === "build" &&
    ((buildLogJobs && buildLogJobs.length > 0) ||
      (qualityGates && qualityGates.length > 0))
  )
    return deriveCIJobSteps(buildLogJobs ?? [], qualityGates);
  if (stepEvents.length > 0) return deriveEventSteps(stepEvents);
  return [];
}
